import React, { useMemo } from 'react';
import { FieldError, Controller } from 'react-hook-form';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import 'react-datepicker/dist/react-datepicker.css';
import fr from 'date-fns/locale/fr';
import { toPickerDate, fromPickerDate } from '@@/utils/date';

const DatePickerInput: React.FC<{
  control: any;
  id: string;
  label: string;
  showTimeSelect?: boolean;
  showTimeSelectOnly?: boolean;
  error?: FieldError;
  placeholder?: string;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  dateOnly?: boolean;
  inputClassName?: string;
  labelClassName?: string;
}> = ({
  control,
  id,
  label,
  showTimeSelect,
  showTimeSelectOnly,
  error,
  placeholder = 'jj/mm/aaaa',
  minDate,
  maxDate,
  disabled,
  dateOnly,
  inputClassName = 'peer block w-full ring-1 ring-gray-200 shadow-sm py-3 px-4 border-main rounded-md placeholder-transparent font-main text-main text-sm',
  labelClassName = 'absolute pointer-events-none font-main text-main text-sm -top-7 left-0 transition-all peer-focus:-top-6 peer-focus:left-0 peer-focus:text-sm peer-placeholder-shown:top-3 peer-placeholder-shown:left-3.5 peer-placeholder-shown:text-base peer-placeholder-shown:italic',
}) => {
  const errorComponent = error?.message && (
    <p className="flex italic font-main text-sm text-red-500 m-1">
      {error.message}
    </p>
  );

  const dateFormat = useMemo(() => {
    if (showTimeSelectOnly) {
      return 'HH:mm';
    }

    return showTimeSelect ? 'dd/MM/yyyy HH:mm' : 'dd/MM/yyyy';
  }, [showTimeSelect, showTimeSelectOnly]);

  return (
    <div className="relative w-full">
      <Controller
        control={control}
        name={id}
        render={({ field }) => {
          const value = dateOnly
            ? toPickerDate(field.value)
            : typeof field.value === 'string'
              ? moment(field.value).toDate()
              : field.value;

          return (
            <DatePicker
              showTimeSelect={!!showTimeSelect}
              showTimeSelectOnly={!!showTimeSelectOnly}
              className={inputClassName}
              placeholderText={placeholder}
              onChange={(date) =>
                field.onChange(dateOnly ? fromPickerDate(date) : date)
              }
              selected={value}
              locale={fr}
              dateFormat={dateFormat}
              timeFormat="HH:mm"
              timeIntervals={15}
              isClearable
              showYearDropdown
              yearDropdownItemNumber={100}
              scrollableYearDropdown
              minDate={minDate}
              maxDate={maxDate}
              disabled={!!disabled}
            />
          );
        }}
      />
      <label htmlFor={id} className={labelClassName}>
        {label}
      </label>
      {errorComponent}
    </div>
  );
};

export default DatePickerInput;
